/**
 * ViewerService — the current logged-in user (platform mode).
 *
 * Mirrors insights-web's pattern: the auth guard fetches the live identity once
 * after sign-in and it's exposed as a signal the shell reads (insights pulls
 * `user { name email }` from `insightsContext`; this app queries the platform's
 * dedicated `viewer` query, which additionally returns `picture` — a signed,
 * directly-usable avatar URL).
 *
 * Demo mode never fetches (PLATFORM_MODE off → no auth, no GqlClient) — the
 * viewer stays null and the avatar falls back to the placeholder.
 */

import { Injectable, inject, signal } from '@angular/core';

import { RDX_VISITORS_ENV } from './env';
import { GqlClient } from './gql-client';

/** The logged-in user, as the shell needs it. */
export interface Viewer {
  id: string;
  name: string | null;
  email: string | null;
  /** Signed profile-photo URL (usable directly as an <img> src), or null. */
  picture: string | null;
  /**
   * Every action this person may take (`viewer.actions`, unscoped): the panel
   * hides what they can't do, e.g. Sign in anyway without `visit:override`.
   */
  actions: readonly string[];
}

/** `viewer` is the platform's current-authenticated-user query (UserType). */
const VIEWER_QUERY = `query Viewer {
  viewer {
    _id
    name
    email
    picture
    actions
  }
}`;

interface ViewerResponse {
  viewer: {
    _id: string;
    name?: string | null;
    email?: string | null;
    picture?: string | null;
    actions?: string[] | null;
  } | null;
}

@Injectable({ providedIn: 'root' })
export class ViewerService {
  private readonly gql = inject(GqlClient);

  private readonly _viewer = signal<Viewer | null>(null);
  /** The current user (null in demo mode, or before the fetch resolves). */
  readonly viewer = this._viewer.asReadonly();

  /**
   * Whether the viewer may take `action`. Demo mode (and `/preview`) plays
   * the spec's operator, who may do everything; `?can=-visit:override` on a
   * preview takes one away, to show what someone without it sees.
   */
  can(action: string): boolean {
    if (!RDX_VISITORS_ENV.PLATFORM_MODE) {
      const denied =
        typeof window === 'undefined'
          ? []
          : (new URLSearchParams(window.location.search).get('can') ?? '')
              .split(',')
              .filter((a) => a.startsWith('-'))
              .map((a) => a.slice(1));
      return !denied.includes(action);
    }
    return this._viewer()?.actions.includes(action) ?? false;
  }

  private loaded = false;
  private inFlight: Promise<void> | null = null;

  /**
   * Fetch the current user once. Single-flight + cached, so repeated calls
   * (the guard runs on every navigation) hit the network at most once. No-op in
   * demo mode. Failures are swallowed — the avatar simply keeps the placeholder.
   */
  ensureLoaded(): Promise<void> {
    if (!RDX_VISITORS_ENV.PLATFORM_MODE || this.loaded) return Promise.resolve();
    this.inFlight ??= this.load().finally(() => {
      this.inFlight = null;
    });
    return this.inFlight;
  }

  private async load(): Promise<void> {
    try {
      const data = await this.gql.gql<ViewerResponse>(VIEWER_QUERY);
      const v = data.viewer;
      if (v) {
        this._viewer.set({
          id: v._id,
          name: v.name ?? null,
          email: v.email ?? null,
          picture: v.picture ?? null,
          actions: v.actions ?? [],
        });
        this.loaded = true;
      }
    } catch {
      // Best-effort — a failed identity fetch must not block the app; the
      // avatar falls back to the placeholder and the menu shows "Account".
    }
  }
}
